/**
 * The page-space ↔ screen-space mapping pdf.js uses (PageViewport), ported so
 * the UI can position form fields and stamps before a render arrives and
 * translate clicks back into PDF points on export. pdf.js does not export its
 * PageViewport class, and the maths is small enough to own.
 */

export type Matrix = [number, number, number, number, number, number];
export type Box = [number, number, number, number];

export interface Viewport {
  width: number;
  height: number;
  transform: Matrix;
}

/** Normalises any rotation (including negatives) to 0 | 90 | 180 | 270. */
export function normalizeRotation(deg: number): number {
  const r = ((Math.round(deg / 90) * 90) % 360 + 360) % 360;
  return r;
}

/**
 * @param view CropBox [x1, y1, x2, y2] in PDF points.
 * @param scale Screen pixels per PDF point.
 * @param rotation Total clockwise display rotation (page /Rotate + user rotation).
 */
export function viewport(view: Box, scale: number, rotation: number): Viewport {
  const r = normalizeRotation(rotation);
  const centerX = (view[2] + view[0]) / 2;
  const centerY = (view[3] + view[1]) / 2;
  let a: number, b: number, c: number, d: number;
  switch (r) {
    case 180: a = -1; b = 0; c = 0; d = 1; break;
    case 90: a = 0; b = 1; c = 1; d = 0; break;
    case 270: a = 0; b = -1; c = -1; d = 0; break;
    default: a = 1; b = 0; c = 0; d = -1;
  }
  let offsetX: number, offsetY: number, width: number, height: number;
  if (a === 0) {
    offsetX = Math.abs(centerY - view[1]) * scale;
    offsetY = Math.abs(centerX - view[0]) * scale;
    width = (view[3] - view[1]) * scale;
    height = (view[2] - view[0]) * scale;
  } else {
    offsetX = Math.abs(centerX - view[0]) * scale;
    offsetY = Math.abs(centerY - view[1]) * scale;
    width = (view[2] - view[0]) * scale;
    height = (view[3] - view[1]) * scale;
  }
  const transform: Matrix = [
    a * scale,
    b * scale,
    c * scale,
    d * scale,
    offsetX - a * scale * centerX - c * scale * centerY,
    offsetY - b * scale * centerX - d * scale * centerY,
  ];
  return { width, height, transform };
}

/** The size in CSS pixels a page occupies at `scale`, honouring rotation. */
export function pageSize(view: Box, rotation: number): { width: number; height: number } {
  const w = view[2] - view[0];
  const h = view[3] - view[1];
  return normalizeRotation(rotation) % 180 === 0 ? { width: w, height: h } : { width: h, height: w };
}

export function toScreen(m: Matrix, x: number, y: number): [number, number] {
  return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}

export function toPdf(m: Matrix, sx: number, sy: number): [number, number] {
  const det = m[0] * m[3] - m[1] * m[2];
  const dx = sx - m[4];
  const dy = sy - m[5];
  return [(m[3] * dx - m[2] * dy) / det, (-m[1] * dx + m[0] * dy) / det];
}

/** Maps a PDF-space rectangle to a screen rectangle {left, top, width, height}. */
export function rectToScreen(m: Matrix, rect: Box) {
  const [x1, y1] = toScreen(m, rect[0], rect[1]);
  const [x2, y2] = toScreen(m, rect[2], rect[3]);
  return {
    left: Math.min(x1, x2),
    top: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}
